import { doc, getDoc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, getMetadata, ref, uploadBytes } from "firebase/storage";
import { app as firebaseApp, auth, db, storage } from "../firebase";
import {
  createThumbnailFile,
  DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
  DEFAULT_TARGET_IMAGE_BYTES,
  optimizeImageFile,
  normalizeManagedImageFilename,
  validateManagedImageFile,
} from "./imageOptimization";
import { IS_DEV_UI_ENABLED, devLog, devWarn } from "./devUi";

export const DEFAULT_INCLUDED_STORAGE_BYTES = 1024 * 1024 * 1024;
export const STORAGE_FILES_COLLECTION = "storageFiles";

export const getStorageFileManifestRef = (workspaceUid, fileId) =>
  doc(db, "workspaces", workspaceUid, STORAGE_FILES_COLLECTION, fileId);

export const getWorkspaceStorageUsageRef = (workspaceUid) =>
  doc(db, "workspaces", workspaceUid, "usage", "storage");

export const formatStorageBytes = (bytes = 0) => {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  return `${(value / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

export const buildManagedImageStorageFileId = ({
  ownerType = "image",
  ownerId = "",
  imageId = "",
  fullPath = "",
} = {}) =>
  [ownerType, ownerId, imageId || fullPath]
    .map((part) => String(part || "").trim().replace(/[^\w-]+/g, "_"))
    .filter(Boolean)
    .join("__")
    .slice(0, 180) || `image_${Date.now()}`;

export const buildThumbnailStoragePath = (fullPath = "") => {
  const cleanPath = String(fullPath || "").replace(/^\/+/, "");
  const slashIndex = cleanPath.lastIndexOf("/");
  if (slashIndex < 0) return `thumbs/${cleanPath}`;
  return `${cleanPath.slice(0, slashIndex)}/thumbs/${cleanPath.slice(slashIndex + 1)}`;
};

const getCategoryKey = (ownerType = "") => {
  switch (ownerType) {
    case "generalQuote":
      return "generalQuoteImages";
    case "curtainQuote":
      return "curtainQuoteImages";
    case "crm":
      return "crmAttachments";
    case "logo":
      return "logos";
    case "fabricLibrary":
      return "fabricPhotos";
    default:
      return "legacy";
  }
};

const getUsageSnapshot = async (workspaceUid) => {
  const usageRef = getWorkspaceStorageUsageRef(workspaceUid);
  const snap = await getDoc(usageRef);
  const data = snap.exists() ? snap.data() || {} : {};
  return {
    ref: usageRef,
    usedBytes: Number(data.usedBytes || 0),
    includedBytes: Number(data.includedBytes || DEFAULT_INCLUDED_STORAGE_BYTES),
    extraBytes: Number(data.extraBytes || 0),
    imageCount: Number(data.imageCount || 0),
    byCategory: data.byCategory || {},
  };
};

export const ensureWorkspaceStorageCapacity = async (workspaceUid, additionalBytes) => {
  const usage = await getUsageSnapshot(workspaceUid);
  const availableBytes = Number(usage.includedBytes || 0) + Number(usage.extraBytes || 0);
  if (availableBytes > 0 && usage.usedBytes + Number(additionalBytes || 0) > availableBytes) {
    const error = new Error("Storage limit reached. Delete old files or upgrade storage.");
    error.code = "storage-limit-reached";
    error.details = {
      usedBytes: usage.usedBytes,
      availableBytes,
      additionalBytes,
    };
    throw error;
  }
  return usage;
};

const writeStorageManifest = async (manifest) => {
  if (!manifest.workspaceUid || !manifest.fileId) return;
  const fileRef = getStorageFileManifestRef(manifest.workspaceUid, manifest.fileId);
  await setDoc(
    fileRef,
    {
      ...manifest,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  const categoryKey = getCategoryKey(manifest.ownerType);
  const totalBytes =
    Number(manifest.sizeBytes || 0) + Number(manifest.thumbnailSizeBytes || 0);
  await setDoc(
    getWorkspaceStorageUsageRef(manifest.workspaceUid),
    {
      usedBytes: increment(totalBytes),
      imageCount: increment(1),
      [`byCategory.${categoryKey}`]: increment(totalBytes),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const archiveManagedStorageFile = async ({
  workspaceUid,
  fileId,
  removedBy = auth.currentUser?.uid || "",
  removalReason = "removed_from_record",
  linkedRecords = null,
} = {}) => {
  if (!workspaceUid || !fileId) return;
  await setDoc(
    getStorageFileManifestRef(workspaceUid, fileId),
    {
      active: false,
      archived: true,
      removedFromRecord: true,
      removedAt: serverTimestamp(),
      removedBy,
      removalReason,
      ...(Array.isArray(linkedRecords) ? { linkedRecords } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const uploadManagedImage = async ({
  workspaceUid,
  file,
  ownerType,
  ownerId = "",
  quoteId = "",
  itemId = "",
  rowId = "",
  leadId = "",
  imageId = "",
  filenamePrefix = "",
  fullPath,
  thumbnailFullPath = "",
  uploadedBy = auth.currentUser?.uid || "",
  metadata = {},
  preserveTransparency = false,
  fullMaxLongEdge = 1600,
  thumbnailMaxLongEdge = 400,
  fullQuality = 0.85,
  thumbnailQuality = 0.78,
  targetBytes = DEFAULT_TARGET_IMAGE_BYTES,
  hardMaxBytes = DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
} = {}) => {
  const validation = validateManagedImageFile(file);
  if (!validation.valid) {
    const error = new Error(validation.message);
    error.code = "invalid-image-file";
    throw error;
  }
  if (!workspaceUid || !fullPath) {
    const error = new Error("Image upload path is missing.");
    error.code = "missing-image-upload-path";
    throw error;
  }

  const uploadedAt = new Date().toISOString();
  const optimized = await optimizeImageFile({
    file,
    maxLongEdge: fullMaxLongEdge,
    quality: fullQuality,
    preserveTransparency,
    targetBytes,
    hardMaxBytes,
  });
  const sourceForThumbnail = optimized.optimizedFile || file;
  const thumbnail = await createThumbnailFile({
    file: sourceForThumbnail,
    maxLongEdge: thumbnailMaxLongEdge,
    quality: thumbnailQuality,
    preserveTransparency,
  });

  const uploadFile = optimized.optimizedFile || file;
  const safeFilename = normalizeManagedImageFilename(uploadFile.name || file.name || "image");
  const resolvedFullPath = String(fullPath || "").replace(
    /[^/]+$/,
    `${filenamePrefix}${safeFilename}`
  );
  const resolvedThumbnailPath = thumbnailFullPath
    ? String(thumbnailFullPath || "").replace(
        /[^/]+$/,
        thumbnail.thumbnailFile
          ? normalizeManagedImageFilename(thumbnail.thumbnailFile.name || safeFilename)
          : safeFilename
      )
    : buildThumbnailStoragePath(resolvedFullPath);
  const additionalBytes =
    Number(uploadFile.size || 0) + Number(thumbnail.thumbnailFile?.size || 0);
  await ensureWorkspaceStorageCapacity(workspaceUid, additionalBytes);

  const fileRef = ref(storage, resolvedFullPath);
  let stage = "upload", uploadCompleted = false, downloadUrlResolved = false;
  const trace = (error = null) => {
    if (ownerType !== "generalQuote") return;
    devLog("GENERAL_IMAGE_OPERATION_TRACE", {
      operation: "upload", stage, workspaceUid, authUid: auth.currentUser?.uid || "",
      userRole: "unknown", membershipValid: null,
      quoteId, itemId, storageBucket: firebaseApp.options.storageBucket || "",
      storageFullPath: resolvedFullPath, fileContentType: uploadFile.type, fileSize: uploadFile.size,
      uploadCompleted, downloadUrlResolved, metadataAttached: false,
      errorCode: error?.code || "", errorMessage: error ? "Image operation failed at this stage." : "",
    });
  };
  trace();
  let uploadSnapshot;
  try {
    uploadSnapshot = await uploadBytes(fileRef, uploadFile, {
    contentType: uploadFile.type,
    customMetadata: {
      workspaceUid,
      ownerType,
      ownerId,
      quoteId,
      itemId,
      rowId,
      leadId,
      imageId,
      uploadedBy,
      uploadedAt,
      compressionStatus: optimized.compressionStatus,
      compressionTargetBytes: String(targetBytes || ""),
      compressionHardMaxBytes: String(hardMaxBytes || ""),
      ...metadata,
    },
    });
    uploadCompleted = true;
  } catch (error) { trace(error); throw error; }
  let downloadURL = "", followupError = null;
  stage = "verify_object";
  try {
    await getMetadata(uploadSnapshot.ref);
    trace();
  } catch (error) {
    trace(error);
    if (ownerType !== "generalQuote") throw error;
    followupError = error;
  }
  stage = "download_url";
  try {
    downloadURL = await getDownloadURL(uploadSnapshot.ref);
    downloadUrlResolved = true;
    trace();
  } catch (error) {
    trace(error);
    if (ownerType !== "generalQuote") throw error;
    followupError = error;
  }

  let thumbnailDownloadURL = "";
  let thumbnailStatus = thumbnail.thumbnailStatus || "failed";
  let thumbnailSizeBytes = 0;
  if (thumbnail.thumbnailFile) {
    try {
      const thumbnailRef = ref(storage, resolvedThumbnailPath);
      const thumbnailSnapshot = await uploadBytes(thumbnailRef, thumbnail.thumbnailFile, {
        contentType: thumbnail.thumbnailFile.type,
        customMetadata: {
          workspaceUid,
          ownerType,
          ownerId,
          quoteId,
          itemId,
          rowId,
          leadId,
          imageId,
          uploadedBy,
          ...(metadata.accountId ? { accountId: metadata.accountId } : {}),
          uploadedAt,
          thumbnail: "true",
        },
      });
      await getMetadata(thumbnailSnapshot.ref);
      thumbnailDownloadURL = await getDownloadURL(thumbnailSnapshot.ref);
      thumbnailStatus = "verified";
      thumbnailSizeBytes = Number(thumbnail.thumbnailFile.size || 0);
    } catch (error) {
      thumbnailStatus = "failed";
      if (IS_DEV_UI_ENABLED) {
        devWarn("MANAGED_IMAGE_THUMBNAIL_UPLOAD_FAILED", {
          code: error?.code,
          message: error?.message,
          fullPath: resolvedFullPath,
          thumbnailFullPath: resolvedThumbnailPath,
        });
      }
    }
  }

  const fileId = buildManagedImageStorageFileId({
    ownerType,
    ownerId,
    imageId,
    fullPath: resolvedFullPath,
  });
  const manifest = {
    fileId,
    workspaceUid,
    ownerType,
    ownerId,
    quoteId,
    itemId,
    rowId,
    leadId,
    imageId,
    bucket: firebaseApp.options.storageBucket || "",
    fullPath: uploadSnapshot.ref?.fullPath || resolvedFullPath,
    thumbnailFullPath: thumbnailStatus === "verified" ? resolvedThumbnailPath : "",
    filename: safeFilename,
    originalFilename: file.name || safeFilename,
    safeFilename,
    contentType: uploadFile.type,
    sizeBytes: uploadFile.size,
    thumbnailSizeBytes,
    originalSizeBytes: optimized.originalSizeBytes,
    optimizedSizeBytes: optimized.optimizedSizeBytes,
    compressionRatio: optimized.compressionRatio,
    compressionTargetBytes: targetBytes,
    compressionHardMaxBytes: hardMaxBytes,
    compressionQuality: optimized.compressionQuality || 0,
    compressionLongEdge: optimized.compressionLongEdge || 0,
    width: optimized.width,
    height: optimized.height,
    originalWidth: optimized.originalWidth,
    originalHeight: optimized.originalHeight,
    uploadedAt,
    uploadedBy,
    active: true,
    archived: false,
    deleted: false,
    removedFromRecord: false,
    linkedRecords: [
      {
        type: ownerType,
        id: ownerId || quoteId || leadId || "",
        quoteId,
        itemId,
        rowId,
        leadId,
      },
    ],
  };
  stage = "manifest";
  try {
    await writeStorageManifest({ ...manifest, createdAt: serverTimestamp() });
    trace();
  } catch (error) {
    trace(error);
    if (ownerType !== "generalQuote") throw error;
    followupError = error;
  }

  return {
    ...manifest,
    id: imageId || fileId,
    imageId: imageId || fileId,
    downloadURL,
    url: downloadURL,
    previewUrl: thumbnailDownloadURL || downloadURL,
    thumbnailDownloadURL,
    thumbnailStatus,
    uploadStatus: followupError ? "uploaded" : "verified",
    resolveStatus: downloadURL ? "resolved" : "unavailable",
    resolveErrorCode: followupError?.code || "",
    resolveErrorMessage: followupError ? "Image uploaded. Preview or storage tracking needs retry; do not upload it again." : "",
    compressionStatus: optimized.compressionStatus,
    extension: safeFilename.includes(".") ? safeFilename.split(".").pop() : "",
  };
};
